import { makeArticle, saintQuestionRelated } from "./learnFactory";

export const pageSaintFeelLike = makeArticle({
  slug: "what-does-saint-tms-feel-like",
  title: "What Does SAINT® TMS Feel Like? | Neuro Wellness",
  description:
    "SAINT® TMS feels like a tapping or knocking sensation on the scalp. Learn what patients experience during sessions, common sensations, and how discomfort is managed.",
  canonical: "/learn/what-does-saint-tms-feel-like",
  h1: "What Does SAINT® TMS Feel Like?",
  breadcrumbLabel: "What Does SAINT® Feel Like?",
  shortAnswer:
    "Most patients describe SAINT® TMS as a tapping or knocking sensation on the scalp during the magnetic pulses. The treatment is delivered while the patient is awake, with no anesthesia or sedation. Headache and scalp discomfort are the most common side effects and usually lessen over the first day or two. Over-the-counter pain relief is often enough to manage any discomfort.",
  keyTakeaways: [
    "SAINT® feels like tapping or knocking on the scalp during pulses.",
    "Patients are awake and alert — no anesthesia or sedation is used.",
    "Headache and scalp discomfort are the most common side effects.",
    "Discomfort usually lessens over the first day or two.",
    "Facial muscle twitching may occur during stimulation.",
  ],
  sections: [
    {
      heading: "The Sensation During Treatment",
      paragraphs: [
        "During each SAINT® session, the electromagnetic coil is placed against the scalp over the targeted brain region. When the magnetic pulses fire, most patients feel a tapping, knocking, or pulsing sensation on the scalp. The coil makes a clicking sound, and hearing protection is worn throughout the session.",
        "The sensation can be surprising at first but most patients adjust within the first few sessions. The intensity can be adjusted by the treatment team if the sensation is too intense. Each session lasts about ten minutes, followed by a roughly 50-minute break.",
      ],
    },
    {
      heading: "Common Sensations and Side Effects",
      paragraphs: [
        "The most commonly reported sensations and side effects include scalp discomfort at the treatment site, headache, and facial muscle twitching during stimulation. These effects are usually mild to moderate and tend to decrease over the first day or two of treatment.",
        "Over-the-counter pain relievers like acetaminophen or ibuprofen are often sufficient for headaches. The treatment team can adjust the coil position or stimulation intensity if discomfort persists. Serious side effects are rare.",
      ],
    },
    {
      heading: "What Happens Between Sessions",
      paragraphs: [
        "Between the ten daily sessions, patients have roughly 50-minute breaks. During these breaks, most patients read, work on a laptop, rest, or talk with family. The clinic provides a comfortable environment. Patients remain awake and alert throughout the day and can eat, drink, and move around normally during breaks.",
      ],
      links: [
        {
          to: "/learn/what-to-expect-during-saint-tms",
          text: "See the full day-by-day guide",
        },
      ],
    },
  ],
  faqs: [
    {
      question: "Does SAINT TMS hurt?",
      answer:
        "Most people do not describe SAINT® as painful. The most common sensation is a tapping or knocking on the scalp during the magnetic pulses. Headache and scalp discomfort are the most frequently reported side effects, usually mild to moderate, and typically lessen over the first day or two. Over-the-counter pain relief is often sufficient.",
    },
    {
      question: "What does SAINT feel like during a session?",
      answer:
        "You will feel a tapping, knocking, or pulsing sensation on the scalp where the coil is positioned. The coil makes a clicking sound, and hearing protection is worn. Facial muscles may twitch during stimulation. Each session lasts about 10 minutes, and you remain awake and alert throughout.",
    },
    {
      question: "Can the stimulation intensity be adjusted?",
      answer:
        "Yes. If the sensation is too intense or uncomfortable, the treatment team can adjust the coil position or the stimulation intensity. The motor threshold — the minimum intensity that causes a visible thumb twitch — is determined at the start of treatment, and the treatment intensity is set relative to that threshold.",
    },
    {
      question: "Will I feel anything between sessions?",
      answer:
        "Most patients feel normal between sessions. Some may have a mild headache or scalp tenderness that persists between sessions, but this usually decreases over the first day or two. The 50-minute breaks are designed for rest, and most patients read, work, or relax during them.",
    },
    {
      question: "Is SAINT painful compared to regular TMS?",
      answer:
        "The sensation is similar to conventional TMS — both use magnetic pulses that produce a tapping feeling on the scalp. The THREE-D trial found that theta burst stimulation (used in SAINT®) was rated slightly more painful than 10 Hz stimulation, but the difference was small and did not affect treatment completion.",
    },
  ],
  sources: [
    {
      citation:
        "Blumberger DM, et al. THREE-D trial. Lancet. 2018;391(10131):1683-1692.",
      url: "https://doi.org/10.1016/S0140-6736(18)30295-2",
    },
  ],
  relatedLinks: saintQuestionRelated,
});
